// src/components/auction/auctionRosterUtils.js
// Helper condivisi per calcolare rose, overrides di ruolo ed economia squadra.
// Usato sia da AuctionDetailPage che da AuctionAnalysisPage.
import { normalizeRoleOverridesInput } from '../../firebase/auctionService';

export const HIDDEN_TEAM_NAME = '__ALTRO__';

export function normalizeNameText(value = '') {
  const normalized = String(value).toLowerCase().trim().normalize('NFD');
  let result = '';
  for (const ch of normalized) {
    const code = ch.codePointAt(0);
    if (code >= 0x0300 && code <= 0x036f) continue;
    result += ch;
  }
  return result;
}

export function splitRoleTokens(roleValue = '') {
  const raw = String(roleValue || '').trim();
  if (!raw) return [];
  return raw
    .split('/')
    .map((part) => part.trim())
    .filter(Boolean);
}

export function getMantraBucket(roleValue = '') {
  const tokens = splitRoleTokens(roleValue);
  if (tokens.includes('P')) return 'P';
  if (tokens.some((t) => ['Dd', 'Dc', 'Ds', 'B'].includes(t))) return 'D';
  if (tokens.some((t) => ['E', 'C', 'M', 'W', 'T'].includes(t))) return 'C';
  if (tokens.some((t) => ['A', 'Pc'].includes(t))) return 'A';
  return 'A';
}

export function buildRoleOverrideMap(auction) {
  const raw = normalizeRoleOverridesInput(auction?.roleOverrides || {});
  const map = {};

  Object.entries(raw).forEach(([name, role]) => {
    const key = normalizeNameText(name);
    const normalizedRole = String(role || '').trim();

    if (!key || !normalizedRole) return;
    map[key] = normalizedRole;
  });

  return map;
}

export function applyRoleOverrideToPlayer(player, roleOverrideMap) {
  if (!player) return player;

  const originalRole = player.originalRole || player.role || null;
  const key = normalizeNameText(player.name || '');
  const overrideRole = roleOverrideMap?.[key];

  return {
    ...player,
    originalRole,
    ...(overrideRole ? { role: overrideRole } : {}),
  };
}

export function applyRoleOverrideToPurchase(purchase, roleOverrideMap) {
  if (!purchase) return purchase;

  const snap = purchase.playerSnapshot || {};
  const originalRole = snap.originalRole || snap.role || purchase.role || null;
  const key = normalizeNameText(snap.name || purchase.playerName || '');
  const overrideRole = roleOverrideMap?.[key];

  return {
    ...purchase,
    ...(overrideRole ? { role: overrideRole } : {}),
    playerSnapshot: {
      ...snap,
      originalRole,
      ...(overrideRole ? { role: overrideRole } : {}),
    },
  };
}

// Campi del giocatore che, a differenza del resto dello snapshot congelato
// all'acquisto, vanno tenuti allineati alla fonte dati live (squadra
// cambiata per un trasferimento, quota di mercato, statistiche che
// avanzano stagione in corso). Il resto dello snapshot (nome, id, ruolo
// classic originale...) resta quello del momento dell'acquisto.
const LIVE_SYNCED_PLAYER_FIELDS = [
  'team',
  'value',
  'goals',
  'assists',
  'ycards',
  'rcards',
  'owngoals',
  'penalties',
  'spenalties',
  'mpenalties',
  'gotgoals',
  'caps',
  'markavg',
  'fmarkavg',
  'last5',
];

export function applyLivePlayerDataToPurchase(purchase, playersById) {
  if (!purchase) return purchase;

  const snap = purchase.playerSnapshot || {};
  const playerId = String(purchase.playerId ?? snap.id ?? '');
  const livePlayer = playerId ? playersById?.[playerId] : null;
  if (!livePlayer) return purchase;

  const patch = {};
  LIVE_SYNCED_PLAYER_FIELDS.forEach((field) => {
    if (livePlayer[field] !== undefined) patch[field] = livePlayer[field];
  });

  return {
    ...purchase,
    ...(patch.team !== undefined ? { playerTeam: patch.team } : {}),
    playerSnapshot: {
      ...snap,
      ...patch,
    },
  };
}

export function getPrimaryRoleValue(roleValue, rolesMode) {
  if (rolesMode !== 'mantra') {
    return String(roleValue || '').trim().toUpperCase() || 'A';
  }
  return getMantraBucket(roleValue);
}

export function isMantraMovementPurchase(roleValue = '') {
  const tokens = splitRoleTokens(roleValue)
    .map((t) => String(t || '').trim())
    .filter(Boolean);

  return !tokens.includes('P');
}

export function buildTeamEconomy({
  teamNames,
  purchases,
  auction,
  rosterLimits,
  rosterMinLimits,
  rolesMode,
}) {
  const initialCredits = Number(auction?.credits || 0);
  const isMantra = rolesMode === 'mantra';

  const mantraMovementLimit = Number(
    auction?.mantraMovementLimit ??
    auction?.rosterLimits?.M ??
    rosterLimits?.M ??
    0
  );

  // Minimo di giocatori di movimento che la rosa deve ancora contenere. Se
  // l'asta non ha un minimo salvato (creata prima di questa feature), il
  // fallback è il massimo stesso: preserva il comportamento storico dove la
  // rosa va riempita esattamente fino al limite.
  const mantraMovementMinLimit = Number(
    auction?.mantraMovementMinLimit ??
    auction?.rosterMinLimits?.M ??
    rosterMinLimits?.M ??
    mantraMovementLimit
  );

  const minLimits = isMantra
    ? {
      P: Number(
        rosterMinLimits?.P ??
        auction?.rosterMinLimits?.P ??
        rosterLimits?.P ??
        auction?.rosterLimits?.P ??
        0
      ),
      M: mantraMovementMinLimit,
    }
    : {
      P: Number(rosterMinLimits?.P ?? rosterLimits?.P ?? 0),
      D: Number(rosterMinLimits?.D ?? rosterLimits?.D ?? 0),
      C: Number(rosterMinLimits?.C ?? rosterLimits?.C ?? 0),
      A: Number(rosterMinLimits?.A ?? rosterLimits?.A ?? 0),
    };

  const createEmptyTeamStats = () => ({
    spent: 0,
    remaining: initialCredits,
    totalPlayers: 0,
    spentByRole: {},
    purchasedByRole: {},
    remainingByRole: isMantra
      ? {
        P: Number(rosterLimits?.P ?? auction?.rosterLimits?.P ?? 0),
        M: mantraMovementLimit,
      }
      : {
        P: Number(rosterLimits?.P ?? 0),
        D: Number(rosterLimits?.D ?? 0),
        C: Number(rosterLimits?.C ?? 0),
        A: Number(rosterLimits?.A ?? 0),
      },
    // Quanti giocatori DEVONO ancora essere acquistati per ruolo per
    // raggiungere il minimo di rosa (a differenza di remainingByRole, che
    // conta la capienza residua fino al massimo).
    remainingMinByRole: { ...minLimits },
  });

  const stats = {};

  teamNames.forEach((team) => {
    stats[team] = createEmptyTeamStats();
  });

  (purchases || [])
    .filter((p) => !p.deleted && p.teamName !== HIDDEN_TEAM_NAME)
    .forEach((purchase) => {
      const team = purchase.teamName || '—';

      if (!stats[team]) {
        stats[team] = createEmptyTeamStats();
      }

      const roleValue = purchase?.playerSnapshot?.role ?? purchase?.role ?? 'A';
      const primaryRole = String(getPrimaryRoleValue(roleValue, rolesMode) || 'A').trim();

      stats[team].spent += Number(purchase.credits || 0);
      stats[team].remaining = Math.max(initialCredits - stats[team].spent, 0);
      stats[team].totalPlayers += 1;
      stats[team].spentByRole[primaryRole] = (stats[team].spentByRole[primaryRole] || 0) + Number(purchase.credits || 0);

      if (rolesMode === 'classic') {
        stats[team].purchasedByRole[primaryRole] = (stats[team].purchasedByRole[primaryRole] || 0) + 1;

        stats[team].remainingByRole[primaryRole] = Math.max(
          (rosterLimits?.[primaryRole] || 0) - stats[team].purchasedByRole[primaryRole],
          0
        );
        stats[team].remainingMinByRole[primaryRole] = Math.max(
          (minLimits?.[primaryRole] || 0) - stats[team].purchasedByRole[primaryRole],
          0
        );
      } else {
        if (isMantraMovementPurchase(roleValue)) {
          stats[team].purchasedByRole.M = (stats[team].purchasedByRole.M || 0) + 1;
        } else {
          stats[team].purchasedByRole.P = (stats[team].purchasedByRole.P || 0) + 1;
        }

        const movementBought = (stats[team].purchasedByRole.M || 0);
        const keeperBought = (stats[team].purchasedByRole.P || 0);

        stats[team].remainingByRole.M = Math.max(mantraMovementLimit - movementBought, 0);
        stats[team].remainingByRole.P = Math.max(
          Number(rosterLimits?.P ?? auction?.rosterLimits?.P ?? 0) - keeperBought,
          0
        );
        stats[team].remainingMinByRole.M = Math.max(minLimits.M - movementBought, 0);
        stats[team].remainingMinByRole.P = Math.max(minLimits.P - keeperBought, 0);
      }
    });

  return stats;
}

export function buildRosterByTeam({ teamNames, purchases }) {
  const map = {};
  teamNames.forEach((name) => {
    map[name] = [];
  });

  (purchases || [])
    .filter((p) => !p.deleted && p.teamName !== HIDDEN_TEAM_NAME)
    .forEach((purchase) => {
      const team = purchase.teamName || '—';
      if (!map[team]) map[team] = [];
      map[team].push(purchase);
    });

  Object.keys(map).forEach((team) => {
    map[team].sort((a, b) => {
      const ra = String(a.playerSnapshot?.role || a.role || '').localeCompare(
        String(b.playerSnapshot?.role || b.role || '')
      );
      if (ra !== 0) return ra;
      return (a.playerSnapshot?.name || a.playerName || '').localeCompare(
        b.playerSnapshot?.name || b.playerName || ''
      );
    });
  });

  return map;
}

export function buildMyRosterPurchases({ rosterByTeam, myTeamName }) {
  return rosterByTeam[myTeamName] || [];
}

// ─── Economia asta a listone ─────────────────────────────────────────────────
// Nel listone conta solo la rosa personale. La rimozione (vendita) di un
// giocatore (deletePurchaseWithRecovery) registra `creditsRecovered`: tornano
// ESATTAMENTE quei crediti, sia meno del pagato (minusvalenza) sia di più
// (plusvalenza: comprato a 1, venduto a 6 → +5 sul budget). Prima la
// differenza era limitata a zero, quindi una vendita in plusvalenza
// restituiva solo il prezzo d'acquisto.
// `listoneCarry` = risultato netto (incassato − pagato) delle vendite
// precedenti dello STESSO giocatore, quando viene ricomprato: il documento
// acquisto è uno per giocatore, e senza questo campo il riacquisto
// cancellerebbe la plus/minusvalenza già realizzata (vedi handlePurchase in
// AuctionDetailPage).
//   speso      = Σ(attivi.(credits − carry)) + Σ(rimossi.(credits − creditsRecovered − carry))
//   disponibili = auction.credits − speso
export function buildListoneEconomy({ auction, purchases = [], rosterLimits = {}, rolesMode = 'classic' }) {
  const initialCredits = Number(auction?.credits || 0);
  const isMantra = rolesMode === 'mantra';
  const list = Array.isArray(purchases) ? purchases : [];

  const active = list.filter((p) => !p.deleted && p.teamName !== HIDDEN_TEAM_NAME);
  const removedWithRecovery = list.filter(
    (p) => p.deleted && p.teamName !== HIDDEN_TEAM_NAME && Number.isFinite(Number(p.creditsRecovered))
  );

  const carryOf = (p) => (Number.isFinite(Number(p.listoneCarry)) ? Number(p.listoneCarry) : 0);
  const spentActive = active.reduce((sum, p) => sum + Number(p.credits || 0) - carryOf(p), 0);
  // Può essere negativo: plusvalenze nette che fanno CRESCERE i disponibili.
  const lostOnRemoval = removedWithRecovery.reduce(
    (sum, p) => sum + Number(p.credits || 0) - Number(p.creditsRecovered || 0) - carryOf(p),
    0
  );

  const spent = spentActive + lostOnRemoval;
  // Correzione manuale dei disponibili (tab Market del listone): l'utente
  // scrive i crediti reali, si salva la differenza rispetto al calcolato
  // (auction.listoneCreditsAdjustment), così acquisti e vendite successivi
  // continuano ad aggiornare il numero partendo dal valore giusto.
  const adjustment = Number.isFinite(Number(auction?.listoneCreditsAdjustment)) ? Number(auction.listoneCreditsAdjustment) : 0;
  const computedAvailable = initialCredits - spent;
  const available = computedAvailable + adjustment;

  const roleKeys = isMantra ? ['P', 'M'] : ['P', 'D', 'C', 'A'];
  const bought = Object.fromEntries(roleKeys.map((r) => [r, 0]));

  active.forEach((purchase) => {
    const roleValue = purchase?.playerSnapshot?.role ?? purchase?.role ?? 'A';
    let key;
    if (isMantra) {
      key = isMantraMovementPurchase(roleValue) ? 'M' : 'P';
    } else {
      key = String(getPrimaryRoleValue(roleValue, rolesMode) || 'A').trim().toUpperCase();
      if (!roleKeys.includes(key)) key = 'A';
    }
    bought[key] = (bought[key] || 0) + 1;
  });

  const byRole = Object.fromEntries(
    roleKeys.map((r) => {
      const limit = Number(rosterLimits?.[r] || 0);
      return [r, { bought: bought[r] || 0, limit, remaining: Math.max(0, limit - (bought[r] || 0)) }];
    })
  );

  const totalLimit = roleKeys.reduce((s, r) => s + Number(rosterLimits?.[r] || 0), 0);

  // Plus/minusvalenze nette di tutte le vendite (incassato − pagato).
  const realized = removedWithRecovery.reduce(
    (sum, p) => sum + Number(p.creditsRecovered || 0) - Number(p.credits || 0) + carryOf(p), 0
  ) + active.reduce((sum, p) => sum + carryOf(p), 0);

  return {
    initialCredits,
    spent,
    available,
    computedAvailable,
    adjustment,
    realized,
    totalPlayers: active.length,
    totalLimit,
    byRole,
  };
}
